<template>
  <div>
    <div class="d-flex align-center">
      <v-avatar class="section__avatar" height="46">
        <v-icon>mdi-check-all</v-icon>
      </v-avatar>
      <span class="pl-3">
        <h2 class="section__title primary--text mb-0 font-weight-bold">
          Enrolment application matches
        </h2>
      </span>
    </div>
    <p class="mt-6">
      The following enrolment applications match the offer details above
      including gender, date of birth, calendar year and scholastic year.
    </p>
    <template v-if="!hasMatches">
      <div
        class="matches__no-results justify-center d-flex flex-column mt-6 rounded"
      >
        <div class="d-flex justify-center pa-0">
          <h3>No matching enrolment applications found.</h3>
        </div>
        <div class="pa-0 mt-4 d-flex justify-center">
          <div class="text-width--max-800">
            <p class="text-center text--wrap-balance mb-0">
              Contact the parent/carer and confirm they have submitted an online
              enrolment application, or contact the census school and confirm
              they have received the application.
            </p>
          </div>
        </div>
      </div>
    </template>
    <v-simple-table class="elevation-2" v-else>
      <thead>
        <tr>
          <th
            v-for="{ value, width, text } in headers"
            :key="value"
            :style="{ width }"
          >
            {{ text }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in cItems" :key="item.applicationID">
          <td>
            {{
              `${item.student.firstName || ''} ${
                item.student.otherName || ''
              } ${item.student.surname || ''}`
            }}
            <div class="table-cell__subtext">
              {{ item.applicationID || '' }}
            </div>
          </td>
          <td>{{ item.schoolName || '' }}</td>
          <td>
            {{
              displayStrictDate(item.dateReceived, 'YYYY-MM-DD', 'DD MMM YYYY')
            }}
          </td>
          <td>
            <Chip
              small
              class="text-center font-weight-bold"
              v-bind="getStatusChipProps(item)"
            ></Chip>
          </td>
          <td>
            <ads-button
              v-if="isMatched"
              @click="handleRowUnmatchButtonClick(item)"
              secondary
              class="ml-0"
              button-text="Unmatch"
            />
            <ads-button
              v-else-if="showMatchButton(item)"
              @click="handleRowMatchButtonClick(item)"
              class="ml-0"
              button-text="Match"
            />
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <app-modal v-if="showMatchModal" data-testid="match-modal">
      <template slot="header">
        <div class="d-flex align-center justify-space-between pt-3 pl-3 w-full">
          <span
            class="font-weight-bold"
            role="heading"
            v-text="
              `Match enrolment application for ${selectedMatch.student.firstName} ${selectedMatch.student.surname}?`
            "
          />
          <v-icon @click="handleCloseMatchModal" class="ml-4 align-self-start">
            mdi-close
          </v-icon>
        </div>
      </template>
      <template slot="body">
        <div class="content mt-3 mx-3">
          <p>
            This selective high school offer will be matched with the enrolment
            application ID <strong v-text="selectedMatch.applicationID" /> at
            census school <strong v-text="selectedMatch.schoolName" />.
          </p>
          <p>
            Once matched, you will gain read-only access to the enrolment
            application details.
          </p>
          <p>
            {{ selectedMatch.schoolName }} will be automatically notified of
            this access.
          </p>
        </div>
      </template>
      <template #footer>
        <div class="mb-0 mr-3 pb-2">
          <ads-button
            button-text="Match application"
            aria-label="Match application"
            @click="handleExecuteMatch"
          />
          <ads-button
            tertiary
            button-text="Cancel"
            aria-label="Cancel"
            @click="handleCloseMatchModal"
          />
        </div>
      </template>
    </app-modal>

    <app-modal v-if="showUnmatchModal" data-testid="unmatch-modal">
      <template slot="header">
        <div
          class="d-flex font-weight-bold align-center justify-space-between pl-3 pt-3 w-full"
        >
          <span role="heading">
            <span class="red--text" v-text="'Unmatch'" /> enrolment application?
          </span>
          <v-icon
            @click="handleCloseUnmatchModal"
            class="ml-4 align-self-start"
          >
            mdi-close
          </v-icon>
        </div>
      </template>
      <template slot="body">
        <div class="content mt-3 mx-3">
          <p>
            This selective high school offer will be unmatched from
            <strong
              v-text="
                `${selectedMatch.student.firstName} ${selectedMatch.student.surname}`
              "
            />'s enrolment application at census school
            <strong v-text="selectedMatch.schoolName" />.
          </p>

          <p>
            You will immediately lose access to view the enrolment application
            details.
          </p>
        </div>
      </template>
      <template #footer>
        <div class="mb-0 mr-3 pb-2">
          <ads-button
            button-text="Unmatch application"
            aria-label="Unmatch application"
            color="red"
            @click="handleExecuteUnmatch"
          />
          <ads-button
            tertiary
            button-text="Cancel"
            aria-label="Cancel"
            @click="handleCloseUnmatchModal"
          />
        </div>
      </template>
    </app-modal>
  </div>
</template>

<script>
// @ts-check
import dayjs from 'dayjs'

import Chip from '@/components/app/ChipNonClickable'
import AppModal from '@/components/app/AppModal'
import { AdsButton } from '@nswdoe/doe-ui-core'

import { SHS_APPLICATION_STATUS_CHIPS, STATUS, STATUS_CHIPS } from '@/constants'
import { stringEqualsCaseInsensitive } from '@/helpers/generalUtils.js'

/**
 * @typedef {Object} header
 * @property {string} text
 * @property {string} value
 * @property {string} [width]
 */
/**
 * @typedef {import('../../types/VirtualSelective.d.ts').MatchListItem} Application
 */
/** @type {header[]} */
const headers = [
  {
    text: 'Application',
    value: 'application'
  },
  {
    text: 'Census school',
    value: 'censusSchool'
  },
  {
    text: 'Submitted',
    value: 'submittedDate',
    width: '150px'
  },
  {
    text: 'Status',
    value: 'status',
    width: '200px'
  },
  {
    text: 'Actions',
    value: 'actions',
    width: '150px'
  }
]

export default {
  name: 'VirtualSelectiveMatches',
  data() {
    const data = {
      headers,
      STATUS_CHIPS,
      showMatchModal: false,
      showUnmatchModal: false,
      /** @type {Application} */
      selectedMatch: null
    }
    return data
  },
  components: {
    Chip,
    AppModal,
    AdsButton
  },
  props: {
    /** @type {import('vue').PropOptions<Application[]>} */
    items: {
      type: Array,
      default: () => []
    },
    isMatched: {
      type: Boolean,
      default: () => false
    },
    isUnmatched: {
      type: Boolean,
      default: () => false
    }
  },
  computed: {
    hasMatches() {
      return this.items.length > 0
    },
    /**
     * @returns {Application[]}
     */
    cItems() {
      return this.items
    }
  },
  methods: {
    /**
     * @param {Application} item
     * @returns {Object}
     */
    getStatusChipProps({ applicationStatus, censusERNStatus }) {
      const { WITHDRAWN, WITHDRAWN_DANGER } = SHS_APPLICATION_STATUS_CHIPS
      const withdrawnInOes = stringEqualsCaseInsensitive(
        applicationStatus,
        STATUS.WITHDRAWN
      )
      const withdrawnInErn = stringEqualsCaseInsensitive(
        censusERNStatus,
        STATUS.WITHDRAWN
      )
      const withdrawnAnywhere = withdrawnInOes || withdrawnInErn
      if (!STATUS_CHIPS[applicationStatus]) {
        return {}
      }
      if (this.isMatched && withdrawnAnywhere) {
        return WITHDRAWN_DANGER
      }
      if (this.isUnmatched && withdrawnInErn) {
        return WITHDRAWN
      }
      return STATUS_CHIPS[applicationStatus]
    },

    /**
     * @param {Application} item
     * @returns {boolean}
     */
    showMatchButton({ applicationStatus, censusERNStatus }) {
      if (
        stringEqualsCaseInsensitive(applicationStatus, STATUS.WITHDRAWN) ||
        stringEqualsCaseInsensitive(censusERNStatus, STATUS.WITHDRAWN)
      ) {
        return false
      }
      return true
    },

    /**
     * @param {Application} match
     */
    handleRowMatchButtonClick(match) {
      this.selectedMatch = match
      this.showMatchModal = true
    },

    /**
     * @param {Application} match
     */
    handleRowUnmatchButtonClick(match) {
      this.selectedMatch = match
      this.showUnmatchModal = true
    },

    handleCloseUnmatchModal() {
      this.selectedMatch = null
      this.showUnmatchModal = false
    },

    handleCloseMatchModal() {
      this.selectedMatch = null
      this.showMatchModal = false
    },

    handleExecuteMatch() {
      this.showMatchModal = false
      this.$emit('matchSharedEnrolment', this.selectedMatch)
    },

    handleExecuteUnmatch() {
      this.showUnmatchModal = false
      this.$emit('unmatchSharedEnrolment', this.selectedMatch)
    },

    /**
     * @param {string} date
     * @param {string} inFormat
     * @param {string} outFormat
     * @returns {string}
     */
    displayStrictDate(
      date,
      inFormat = 'YYYY-MM-DD',
      outFormat = 'DD MMM YYYY'
    ) {
      const dDayjs = dayjs(date, inFormat, true)
      if (dDayjs.isValid()) {
        return dDayjs.format(outFormat)
      }
      return ''
    }
  }
}
</script>

<style lang="scss">
.w-full {
  width: 100%;
}

.table-cell__subtext {
  color: $grey-1;
  font-size: 14px;
}

.text--wrap-balance {
  text-wrap: balance;
}

.text-width--max-800 {
  max-width: 800px;
}

.matches__no-results {
  min-height: 220px;
  line-height: 1.5;
  border: 2px solid $ads-grey-03;
  & h3 {
    font-size: 1.25rem;
  }
}

.section__title {
  font-weight: 1.5rem;
  line-height: 2.25rem;
}

.section__avatar {
  background-color: $ads-primary-teal;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  color: $ads-navy;
}

.v-data-table > .v-data-table__wrapper > table {
  .v-chip__content span {
    display: inline-block;
    text-align: center;
    min-width: 135px;
    font-weight: bold;
  }
  & > * > tr > *:first-child {
    padding-left: 26px;
  }
  & > * > tr > *:last-child {
    padding-right: 26px;
  }
  & > thead > tr > th {
    font-weight: bold;
    font-size: 16px;
    padding-top: 18px;
    padding-bottom: 18px;
    vertical-align: top;
  }
  & > tbody > tr > td {
    font-size: 16px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
}
</style>
